import { useStrings } from "@1js/localization-components";
import {
  Dropdown,
  Option,
  Skeleton,
  SkeletonItem,
  makeStyles,
} from "@fluentui/react-components";
import React from "react";
import type { Appearance } from "../data";
import {
  AllLabel,
  CreatedByAllLabel,
  CreatedByMeLabel,
  CreatedByMicrosoftLabel,
  FilterLabel,
  MeLabel,
  MicrosoftLabel,
} from "./SavedPromptsFilter.strings";

export type SavedPromptsFiltersProps = {
  isLoading?: boolean;
  appearance?: Appearance;
  value?: SavedPromptsFilters;
  onFilterChanged?: (filter: SavedPromptsFilters) => void;
};

export type SavedPromptsFilters =
  | "CreatedByAll"
  | "CreatedByMe"
  | "CreatedByMicrosoft";

const useStyles = makeStyles({
  dropdown: {
    minWidth: "180px",
  },
});

export const SavedPromptsFilter: React.FC<SavedPromptsFiltersProps> =
  React.memo(({ isLoading, value, onFilterChanged }) => {
    value = value || "CreatedByAll";
    const localizations = useStrings({
      FilterLabel,
      AllLabel,
      MeLabel,
      MicrosoftLabel,
      CreatedByAllLabel,
      CreatedByMeLabel,
      CreatedByMicrosoftLabel,
    });
    const options: Record<SavedPromptsFilters, string> = {
      CreatedByAll: localizations.AllLabel(),
      CreatedByMe: localizations.MeLabel(),
      CreatedByMicrosoft: localizations.MicrosoftLabel(),
    };
    const optionsText: Record<SavedPromptsFilters, string> = {
      CreatedByAll: localizations.CreatedByAllLabel(),
      CreatedByMe: localizations.CreatedByMeLabel(),
      CreatedByMicrosoft: localizations.CreatedByMicrosoftLabel(),
    };
    const defaultStyles = useStyles();

    const handleOnOptionSelect = (value: SavedPromptsFilters) => {
      onFilterChanged && onFilterChanged(value);
    };

    return isLoading ? (
      <Skeleton>
        <SkeletonItem size={32} style={{ width: "160px" }} />
      </Skeleton>
    ) : (
      <Dropdown
        placeholder={localizations.FilterLabel()}
        onOptionSelect={(_, data) =>
          handleOnOptionSelect(data.optionValue as SavedPromptsFilters)
        }
        multiselect={false}
        selectedOptions={[value]}
        value={optionsText[value]}
        className={defaultStyles.dropdown}
      >
        {Object.entries(options).map(([value, text]) => (
          <Option key={value} value={value}>
            {text}
          </Option>
        ))}
      </Dropdown>
    );
  });

SavedPromptsFilter.displayName = "SavedPromptsFilters";
